<template>
	<MStateBox :loading="!initialized" :empty="emptyData" class="notice-wrap">
		<CTabs
			v-if="tabOption.length > 1"
			class="c-tab"
			v-model="activeTab"
			:option="tabOption"
			:ellipsis="false"
			@change="changeActive"
		/>

		<CScrollPro
			class="scroll-wrap"
			ref="scrollRef"
			pullDown
			@pull-down="scrollPullDown"
			pullUp
			:moreData="currentListData.hasMore"
			@pull-up="scrollPullUp"
			contentClassName="act-scroll-box"
		>
			<div class="item-wrap" v-if="currentListData.list.length">
				<CCardNoticePanel
					v-for="(item, idx) in currentListData.list"
					:key="`${activeTab}-${idx}-${item.id || idx}`"
					:item="item"
					:border="idx != currentListData.list.length - 1"
				></CCardNoticePanel>
			</div>
			<m-visible-tips v-if="!currentListData.emptyShow && !currentListData.hasMore" />
			<div v-if="currentListData.emptyShow" class="empty-box">
				<CFullEmpty />
			</div>
		</CScrollPro>
	</MStateBox>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick, getCurrentInstance } from 'vue';
import CTabs from '@/view/enterprise/components/c-tabs.vue';
import CScrollPro from '@/view/enterprise/components/c-scroll-pro.vue';
import CCardNoticePanel from '@rebuild/components/CCardNoticePanel.vue';
import CFullEmpty from '@/view/enterprise/components/c-full-empty.vue';
import { miniAwaitTo } from '@/utils/api';
import { useRoute } from '@/utils/vue-api';
import i18n from '@/assets/js/i18n';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';
import MStateBox from '@/view/enterprise_rebuild/components/MStateBox.vue';
import { useCommonData } from '@rebuild/hooks/useCommonData';

// 获取当前实例以使用 proxy.$set
const { proxy } = getCurrentInstance();

const { isChemExpress, isFlowTmpl } = useCommonData();

// 数据层
const { announcement, announcement_tabs } = useModuleData(); // 变量名与 loaderFactories key 一致

const route = useRoute();

const activeTab = ref(0);
const tabOption = ref([]);
const ANNOUNCEMENT_RATE = ref([]);
const storeId = ref(route.query.storeId);
const listData = ref([
	{
		list: [],
		pagestart: 0,
		hasMore: true,
		classificationIds: '',
		emptyShow: false
	}
]);
const initialized = ref(false);

// 模板引用
const scrollRef = ref(null);

// 空状态判断 - 基于数据层状态和初始化状态
const emptyData = computed(() => {
	return announcement_tabs.empty || tabOption.value.length === 0;
});

const isChemExpressAndFlowTmpl = computed(() => isChemExpress.value && isFlowTmpl.value);

// 当前激活的列表数据
const currentListData = computed(() => {
	const data = listData.value[activeTab.value];
	return (
		data || {
			list: [],
			pagestart: 0,
			hasMore: true,
			classificationIds: '',
			emptyShow: false
		}
	);
});

// 初始化方法
async function init() {
	// 使用 announcement_tabs 获取 tab 配置
	const [err, res] = await miniAwaitTo(announcement_tabs.refresh());
	if (err || !announcement_tabs.data?.data) {
		initialized.value = true; // 标记初始化完成，即使失败
		return;
	}

	tabOption.value = (announcement_tabs.data.data || [])
		.sort((a, b) => a.sort - b.sort)
		.filter(v => v.isPublish)
		.map(v => {
			ANNOUNCEMENT_RATE.value.push(v);
			return v.name;
		});

	// 皓元医药定制化，ESG特殊处理
	if (isChemExpressAndFlowTmpl.value) {
		tabOption.value.push('ESG');
		ANNOUNCEMENT_RATE.value.push({
			id: 'ESG',
			name: 'ESG',
			isPublish: true,
			sort: 100
		});
	}

	initTab();
	loadData();
	initialized.value = true;
}

function initTab() {
	const { active } = route.query;
	if (active) {
		const findTabIndex = ANNOUNCEMENT_RATE.value.findIndex(v => v.id == active);
		if (findTabIndex != -1) {
			activeTab.value = findTabIndex;
		}
	}

	if (tabOption.value.length) {
		listData.value = ANNOUNCEMENT_RATE.value.map((item, index) => {
			const currId = item.id;
			let currTabIds =
				currId != 'announcement_all'
					? [currId, ...(item.subTabList || []).map(sub => sub.id)]
							.map(v => v.slice(11).replace(/,/g, '#'))
							.join(',')
					: '';

			if (isChemExpressAndFlowTmpl.value && currId === 'ESG') {
				currTabIds = 'ESG';
			}
			return {
				list: [],
				pagestart: 0,
				hasMore: true,
				classificationIds: !currId ? '' : currTabIds,
				emptyShow: false
			};
		});
		console.log('🚀 ~ initTab ~ listData.value:', listData.value);
	} else {
		// 没有Tab时，就请求全部数据
		listData.value = [
			{
				list: [],
				pagestart: 0,
				hasMore: true,
				classificationIds: '',
				emptyShow: false
			}
		];

		// 皓元医药定制化，ESG特殊处理
		if (isChemExpressAndFlowTmpl.value) {
			listData.value = [
				{
					list: [],
					pagestart: 0,
					hasMore: true,
					classificationIds: 'ESG',
					emptyShow: false
				}
			];
		}
	}
}

function changeActive(index) {
	activeTab.value = index;

	// 重新初始化滚动组件
	scrollRef.value && scrollRef.value.init();

	if (currentListData.value.list.length == 0 && currentListData.value.hasMore) {
		loadData(true);
	}
}

async function loadData(reload = false) {
	let target = listData.value[activeTab.value];

	// 确保 target 存在
	if (!target) {
		console.warn('Target data not found for activeTab:', activeTab.value);
		return;
	}

	let params = {
		pageStart: target.pagestart,
		pageNum: 20,
		classificationIds: target.classificationIds
	};

	// 皓元医药定制化，ESG特殊处理
	if (isChemExpressAndFlowTmpl.value && target.classificationIds === 'ESG') {
		params.title = 'ESG';
		delete params.classificationIds;
	}
	// 使用 useModuleData 的数据刷新方法
	await announcement.refresh(params);

	// 保持与历史组件相同的数据访问模式
	const data = announcement.data?.data || [];
	const extra = announcement.data?.extra || { hasMore: false, total: 0 };

	const newData = [...target.list, ...data];
	// 创建新的对象来确保响应式更新
	const updatedTarget = {
		...target,
		list: reload ? data : newData,
		hasMore: extra.hasMore,
		emptyShow: newData.length == 0
	};

	// 使用 proxy.$set 强制触发响应式更新
	proxy.$set(listData.value, activeTab.value, updatedTarget);

	// 确保响应式更新
	await nextTick();
}

async function scrollPullDown(done) {
	setPageStart('pullDown');
	await loadData(true);
	done();
}

async function scrollPullUp(done) {
	setPageStart('pullUp');
	await loadData();
	done();
}

function setPageStart(type) {
	let _activeTab = activeTab.value;
	let target = listData.value[_activeTab];

	// 确保 target 存在
	if (!target) {
		console.warn('Target data not found for activeTab:', _activeTab);
		return;
	}

	// 创建新的对象来确保响应式更新
	const updatedTarget = {
		...target,
		pagestart: type == 'pullDown' ? 0 : target.pagestart + 1
	};

	// 使用 proxy.$set 强制触发响应式更新
	proxy.$set(listData.value, _activeTab, updatedTarget);
}

onMounted(() => {
	init();
});
</script>

<style lang="less" scoped>
.notice-wrap {
	height: 100%;
	background: #fff;
	/deep/.module-state-content {
		display: flex;
		flex-direction: column;
	}
	.scroll-wrap {
		flex: 1;
		height: 0;
	}
	.item-wrap {
		padding: 0.14rem 0.14rem 0;
	}
	.empty-box {
		height: calc(100vh - 0.4rem);
	}
}
</style>
